import {
  Connection,
  PublicKey,
  Signer,
  TransactionInstruction,
  BlockhashWithExpiryBlockHeight,
  sendAndConfirmRawTransaction,
  TransactionMessage,
  VersionedTransaction,
  Transaction,
  AddressLookupTableAccount,
} from '@solana/web3.js';
import { Instruction, TransactionPayload } from './instruction';
import { Owner } from './Owner';
import { signTransaction } from './transactionHelpers';

export class TransactionBuilder {
  private connection: Connection;
  private feePayer: PublicKey;
  private instructions: Instruction[];
  private owner: Owner;

  constructor(connection: Connection, feePayer: PublicKey, owner: Owner) {
    this.connection = connection;
    this.feePayer = feePayer;
    this.instructions = [];
    this.owner = owner;
  }

  addInstruction(instruction: Instruction): TransactionBuilder {
    this.instructions.push(instruction);
    return this;
  }

  async build(
    blockhashWithExpiryBlockHeight: BlockhashWithExpiryBlockHeight,
    addressLookupTables: AddressLookupTableAccount[],
    asLegacyTransaction?: boolean,
  ): Promise<TransactionPayload> {
    let instructions: TransactionInstruction[] = [];
    let setupInstructions: TransactionInstruction[] = [];
    let cleanupInstructions: TransactionInstruction[] = [];
    let signers: Signer[] = [];
    this.instructions.forEach((curr) => {
      setupInstructions = setupInstructions.concat(curr.setupInstructions);
      instructions = instructions.concat(curr.instructions);
      cleanupInstructions = cleanupInstructions.concat(curr.cleanupInstructions);
      signers = signers.concat(curr.signers);
    });

    const transactionInstructions = setupInstructions.concat(instructions).concat(cleanupInstructions);

    const transaction = asLegacyTransaction
      ? this.buildLegacyTransaction({ transactionInstructions, blockhashWithExpiryBlockHeight })
      : this.buildVersionedTransaction({
          transactionInstructions,
          blockhashWithExpiryBlockHeight,
          addressLookupTables,
        });

    return {
      transaction,
      signers,
      execute: this.owner.isKeyPair
        ? () => {
            const signature = signTransaction(transaction, [this.owner.signer!, ...signers]);
            const serializedTx = transaction.serialize();
            return sendAndConfirmRawTransaction(this.connection, Buffer.from(serializedTx), {
              signature,
              ...blockhashWithExpiryBlockHeight,
            });
          }
        : async () => {
            throw new Error('Please use a Keypair for the owner parameter to enable the execute function');
          },
    };
  }

  private buildLegacyTransaction({
    transactionInstructions,
    blockhashWithExpiryBlockHeight,
  }: {
    transactionInstructions: TransactionInstruction[];
    blockhashWithExpiryBlockHeight: BlockhashWithExpiryBlockHeight;
  }) {
    return new Transaction({ feePayer: this.feePayer, ...blockhashWithExpiryBlockHeight }).add(
      ...transactionInstructions,
    );
  }

  private buildVersionedTransaction({
    transactionInstructions,
    blockhashWithExpiryBlockHeight,
    addressLookupTables,
  }: {
    transactionInstructions: TransactionInstruction[];
    blockhashWithExpiryBlockHeight: BlockhashWithExpiryBlockHeight;
    addressLookupTables: AddressLookupTableAccount[];
  }) {
    const transactionMessage = new TransactionMessage({
      payerKey: this.feePayer,
      instructions: transactionInstructions,
      recentBlockhash: blockhashWithExpiryBlockHeight.blockhash,
    });

    const messageV0 = transactionMessage.compileToV0Message(addressLookupTables);
    return new VersionedTransaction(messageV0);
  }
}
